import { useEffect, useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { type DialogProps, FormControl, Stack } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import axios from 'axios';
import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { type ClassSessionInfo } from '../../context/types';
import { type SchoolSettings } from '../../types/apiTypes';
import { getUrls } from '../../utils/url';
import { ConfirmationDialog } from '../common/dialog/ConfirmationDialog';
import ControlledCheckbox from '../forms/ControlledCheckbox';
import { ControlledDateTimePicker } from '../forms/ControlledDateTimePicker';
import ControlledInput from '../forms/ControlledInput';

export type EditSessionDialogProps = DialogProps & {
    session?: ClassSessionInfo;
    sessions: ClassSessionInfo[] | undefined;
    settings: SchoolSettings | null;
    userUuid: string;
    updateSessions: (sessions: ClassSessionInfo[]) => void;
};

interface DefaultValues {
    topic: string;
    disableAttendance: boolean;
    hasAdjustedStartTime: boolean | undefined;
    attendanceStartTime: dayjs.Dayjs | null;
}

const getDefaultValues = (session?: ClassSessionInfo): DefaultValues => {
    const hasAdjustedStartTime = session && session?.attendance_start_time !== null;
    return {
        topic: session?.class_session_topic ?? '',
        disableAttendance: session?.disable_attendance ?? false,
        hasAdjustedStartTime,
        attendanceStartTime: hasAdjustedStartTime
            ? session?.attendance_start_time &&
              session?.attendance_start_time !== '' &&
              session?.attendance_start_time !== null
                ? dayjs(session.attendance_start_time)
                : dayjs(Date.now())
            : null
    };
};

export const EditSessionDialog = (props: EditSessionDialogProps) => {
    const { open, onClose, session, sessions, settings, updateSessions, userUuid } = props;
    const { api } = getUrls();
    const { t, i18n } = useTranslation();

    const schema = yup.object({
        topic: yup.string().notRequired(),
        disableAttendance: yup.boolean(),
        hasAdjustedStartTime: yup.boolean(),
        attendanceStartTime: yup
            .mixed()
            .notRequired()
            .test('is-valid-date', t('class:invalid-date'), (value: any) => {
                if (value === null || value === undefined || value === '') {
                    // Allow null, undefined, or empty string
                    return true;
                }
                if (typeof value === 'string') {
                    // Check if the value is a valid date string
                    const parsedDate = dayjs(value);
                    return parsedDate.isValid();
                }
                if (dayjs.isDayjs(value)) {
                    // Check if the value is a valid Dayjs object
                    return value.isValid();
                }
                return false; // Invalid value
            })
        // confirm that the date is either null or that the value is Dayjs and is valid date
    });

    const defaultValues = getDefaultValues(session);
    const formRef = useRef<HTMLFormElement>(null);
    const [confirming, setConfirming] = useState(false);

    // Note - isValid must be deconstructed here or it will always be false:
    // https://github.com/react-hookform/react-hookform/issues/2755#issuecomment-962274624
    const {
        handleSubmit,
        control,
        formState: { isValid },
        reset,
        watch
    } = useForm({ defaultValues, resolver: yupResolver(schema), shouldUnregister: true });

    useEffect(() => {
        // Update the form with new default values when the session changes.
        reset(getDefaultValues(session));
    }, [reset, session]);

    const handleClose = () => {
        onClose?.({}, 'backdropClick');
    };

    const onSubmit = async (data: DefaultValues) => {
        if (!isValid) {
            return;
        }
        if (!sessions) {
            throw Error('Sessions failed to load');
        }
        if (!session) {
            throw Error('Session is undefined');
        }
        const attendanceStartTime = data.hasAdjustedStartTime ? data.attendanceStartTime || null : null;
        const params = {
            user_uuid: userUuid,
            session: {
                class_session_topic: data.topic,
                class_session_uuid: session?.class_session_uuid,
                class_uuid: session?.class_uuid,
                disable_attendance: data.disableAttendance || false,
                attendance_start_time: data.hasAdjustedStartTime ? data.attendanceStartTime?.toISOString() : null
            }
        };

        const modifiedSession = sessions.find((s) => s.sid === session?.sid);

        if (!modifiedSession) {
            throw Error(`Failed to find session with id ${session?.sid}`);
        }
        modifiedSession.class_session_topic = data.topic;
        modifiedSession.disable_attendance = data.disableAttendance || false;
        modifiedSession.attendance_start_time = data.hasAdjustedStartTime
            ? data.attendanceStartTime?.toISOString() || null
            : null;

        session.class_session_topic = modifiedSession.class_session_topic;
        session.disable_attendance = modifiedSession.disable_attendance;
        session.attendance_start_time = modifiedSession.attendance_start_time;

        updateSessions(sessions); // this will rerun the session sorting work.
        setConfirming(true);

        try {
            await axios.put(`${api}/v1/class/sessions`, params);
        } catch (e) {
            console.error(e);
        }

        reset(getDefaultValues(session)); // Reset the form with the updated session
        setConfirming(false);
        handleClose();
    };

    const submitForm = () => {
        formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    };

    const handleEditDialogConfirmed = () => {
        submitForm();
    };

    return (
        <ConfirmationDialog
            id="edit-session"
            open={open}
            onClose={handleClose}
            title={t('calendar:edit-class-session')}
            confirmLabel={t('calendar:save')}
            onConfirm={handleEditDialogConfirmed}
            confirming={confirming}
            setConfirming={setConfirming}
        >
            <Stack>
                <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
                    <FormControl fullWidth>
                        <ControlledInput type="text" name="topic" control={control} label={t('class:topic')} />
                    </FormControl>
                    {settings?.options?.enablePerSessionAttendanceOptions && (
                        <>
                            <FormControl fullWidth>
                                <ControlledCheckbox
                                    name="disableAttendance"
                                    disableFocusRipple
                                    control={control}
                                    label={t('class:disable-attendance')}
                                    hint={t('class:attendance-options.disable-attendance-hint')}
                                />
                            </FormControl>
                            <FormControl>
                                <ControlledCheckbox
                                    name="hasAdjustedStartTime"
                                    disableFocusRipple
                                    control={control}
                                    label={t('class:attendance-options.adjusted-start-time')}
                                    hint={t('class:attendance-options.adjusted-start-time-hint')}
                                />
                            </FormControl>
                            {watch('hasAdjustedStartTime') && (
                                <FormControl>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <ControlledDateTimePicker
                                            name="attendanceStartTime"
                                            control={control}
                                            label={t('class:attendance-options.set-adjusted-start-time')}
                                        />
                                    </LocalizationProvider>
                                </FormControl>
                            )}
                        </>
                    )}
                </form>
            </Stack>
        </ConfirmationDialog>
    );
};
